/**
 * Página: Manifiestos de guías (una card por transportadora)
 */
import React, { useCallback, useMemo, useState } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { Button } from '../../components/ui'
import ConfirmationModal from '../../components/modals/ConfirmationModal'
import { ManifiestosGrid } from '../../components/manifiestos'
import {
  useGenerateManifiesto,
  useManifiestosCounts,
} from '../../hooks/manifiestos'
import type { ManifiestoCarrierConfig } from '../../types/manifiestos'

function formatUpdatedAt(date: Date | null): string {
  if (!date) return 'Sin actualizar'
  return date.toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

const Manifiestos: React.FC = () => {
  const { carriers, counts, orders, isRefreshing, lastUpdatedAt, refresh } =
    useManifiestosCounts()

  const { generate, generatingCarrierId } = useGenerateManifiesto(() => {
    void refresh()
  })

  const [selectedCarrier, setSelectedCarrier] =
    useState<ManifiestoCarrierConfig | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const selectedCount = useMemo(() => {
    if (!selectedCarrier) return 0
    return (
      counts.find((item) => item.carrierId === selectedCarrier.id)?.count ?? 0
    )
  }, [counts, selectedCarrier])

  const handleGenerateRequest = useCallback(
    (carrier: ManifiestoCarrierConfig) => {
      setSelectedCarrier(carrier)
      setShowConfirmModal(true)
    },
    []
  )

  const handleCloseModal = useCallback(() => {
    if (generatingCarrierId) return
    setShowConfirmModal(false)
    setSelectedCarrier(null)
  }, [generatingCarrierId])

  const handleConfirmGenerate = useCallback(async () => {
    if (!selectedCarrier) return
    const carrier = selectedCarrier
    setShowConfirmModal(false)
    setSelectedCarrier(null)
    await generate(carrier, orders)
  }, [selectedCarrier, generate, orders])

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold">
            Manifiestos de guías
          </h2>
{/*           <p className="text-sm text-gray-500 mt-1">
            Pedidos en estado &quot;Listo para despachar&quot;, una sección por
            transportadora.
          </p> */}
          <p className="text-xs text-gray-400 mt-2">
            Última actualización: {formatUpdatedAt(lastUpdatedAt)}
          </p>
        </div>

        <Button
          variant="secondary"
          outline
          isLoading={isRefreshing}
          leftIcon={ArrowPathIcon}
          onClick={() => void refresh()}
          aria-label="Actualizar conteos de manifiestos"
        >
          Actualizar
        </Button>
      </div>

      <ManifiestosGrid
        carriers={carriers}
        counts={counts}
        isRefreshing={isRefreshing}
        generatingCarrierId={generatingCarrierId}
        onGenerate={handleGenerateRequest}
      />

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCloseModal}
        title="Generar manifiesto"
        message={
          selectedCarrier
            ? `Se generará el manifiesto de ${selectedCarrier.label} con ${selectedCount} despacho${selectedCount === 1 ? '' : 's'}. Los pedidos pasarán a estado Manifiesto impreso.`
            : ''
        }
        type="warning"
        confirmText="Confirmar e imprimir"
        cancelText="Cancelar"
        showCancel
        onConfirm={() => void handleConfirmGenerate()}
      />
    </div>
  )
}

export default Manifiestos
